// OperatorFairnessModulePage.jsx
import React, {
  Fragment,
  useMemo,
  useState
} from "react";

import {
  AnimatePresence,
  motion,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform
} from "framer-motion";

import {
  ArrowRight,
  BadgeCheck,
  Ban,
  BookOpenCheck,
  Check,
  ChevronLeft,
  ChevronRight,
  CircleDot,
  ClipboardCheck,
  Code2,
  Database,
  Eye,
  FileCheck2,
  Fingerprint,
  FlaskConical,
  Gauge,
  GitCommitHorizontal,
  Hash,
  KeyRound,
  Layers3,
  LockKeyhole,
  Microscope,
  ReceiptText,
  RefreshCcw,
  Scale,
  ScanSearch,
  Server,
  ShieldCheck,
  Sparkles,
  Stamp,
  Terminal,
  UserCheck,
  Users,
  WalletCards,
  Waypoints,
  X,
  Zap
} from "lucide-react";

import "./Styles.css";
import HeaderToMainPage from "../../Universal/Header/Index";
import Footer from "../../Universal/Footer/Index";
import { Link } from "react-router-dom";


/* ============================================================
   CONTENT
============================================================ */

const proofRounds = [
  {
    id: "mines",
    index: "ROUND_8A91",
    game: "MINES",
    tenant: "tenant_alpha",
    ruleset: "mines.v4.2",
    commitment: "7b9f…1a42",
    clientSeed: "player-2026-09",
    nonce: "184",
    result: "safe: 2,7,11 • mine: 13",
    status: "MATCH"
  },
  {
    id: "dice",
    index: "ROUND_90D2",
    game: "DICE",
    tenant: "tenant_alpha",
    ruleset: "dice.v3.8",
    commitment: "3c14…aa90",
    clientSeed: "verify-me",
    nonce: "185",
    result: "roll: 62.47",
    status: "MATCH"
  },
  {
    id: "pattern",
    index: "ROUND_A012",
    game: "PATTERN_LOCK",
    tenant: "tenant_beta",
    ruleset: "pattern.v2.1",
    commitment: "91de…7c03",
    clientSeed: "user-seed-42",
    nonce: "021",
    result: "sequence: 1→5→9→6→2",
    status: "MATCH"
  }
];


const ethicRules = [
  {
    id: "proof",
    number: "01",
    icon: Fingerprint,
    title: "Proof should exist before a dispute.",
    statement:
      "If verification only appears after a player complains, transparency is reactive theater.",
    implementation:
      "Supported first-party rounds carry structured identifiers, commitment context, ruleset lineage and replayable artifacts as part of the round lifecycle."
  },
  {
    id: "authority",
    number: "02",
    icon: Server,
    title: "The UI should never be the authority.",
    statement:
      "A browser animation is not a result. A client-side number is not settlement.",
    implementation:
      "Round admission, authoritative resolution and settlement stay server-side. The interface presents the result and exposes the evidence."
  },
  {
    id: "risk",
    number: "03",
    icon: Gauge,
    title: "Risk controls belong before the outcome.",
    statement:
      "Operator risk management should not become an excuse to secretly alter a result after a valid wager is accepted.",
    implementation:
      "Wager caps, eligibility, geofencing and other operator controls determine whether an action is admitted before supported first-party resolution."
  },
  {
    id: "player",
    number: "04",
    icon: UserCheck,
    title: "The player should not need a cryptography degree.",
    statement:
      "Transparency that only an engineer can use is incomplete product design.",
    implementation:
      "Verifier surfaces translate round evidence into a readable workflow while preserving deeper artifacts for support, auditors and technical reviewers."
  },
  {
    id: "tenant",
    number: "05",
    icon: Layers3,
    title: "Fairness evidence should stay inside the operator boundary.",
    statement:
      "Multi-tenant efficiency should not turn verification material into shared ambiguity.",
    implementation:
      "Round artifacts, retention posture and review access remain tenant-scoped so one operator can explain its own history."
  },
  {
    id: "ai",
    number: "06",
    icon: Sparkles,
    title: "AI can explain evidence. It does not get to become evidence.",
    statement:
      "A model-generated answer should never be the final authority on RNG, balances or settlement.",
    implementation:
      "Operator intelligence may summarize logs and surface anomalies, while private fairness material, RNG authority, balance mutation and final transactional decisions remain outside AI control."
  }
];


const lifecycle = [
  {
    key: "COMMIT",
    icon: GitCommitHorizontal,
    line: "Commit before resolution",
    copy:
      "A protected server-side input can be represented by a hash commitment before the round resolves, creating an anchor that cannot be conveniently rewritten after the outcome is known."
  },
  {
    key: "RESOLVE",
    icon: Server,
    line: "Resolve on the authoritative layer",
    copy:
      "The supported first-party engine consumes the canonical round inputs and stable ruleset context. The front end does not invent the result."
  },
  {
    key: "SETTLE",
    icon: WalletCards,
    line: "Record the money consequence",
    copy:
      "Settlement is attached to the same authoritative event history so financial effects can be traced back to the game event that produced them."
  },
  {
    key: "REVEAL",
    icon: KeyRound,
    line: "Expose what can safely be verified",
    copy:
      "After the relevant lifecycle boundary, verification material can be surfaced without giving the player pre-round access to protected secrets."
  },
  {
    key: "REPRODUCE",
    icon: RefreshCcw,
    line: "Re-run the evidence",
    copy:
      "The verifier reconstructs the supported round from stored inputs and ruleset context and compares the reproduced result to what the player saw."
  }
];


const evidenceReceipts = [
  {
    id: "player",
    label: "PLAYER",
    icon: Eye,
    headline: "A readable answer to: “Was that round actually the round?”",
    bullets: [
      "Round-specific fairness panel",
      "Commitment and seed context where applicable",
      "Nonce / sequence context",
      "Reproduced result comparison",
      "Historical verification entry point"
    ]
  },
  {
    id: "operator",
    label: "OPERATOR",
    icon: ClipboardCheck,
    headline: "A dispute path based on evidence instead of escalation.",
    bullets: [
      "Tenant-scoped round history",
      "Role-aware review access",
      "Ruleset and configuration context",
      "Settlement lineage",
      "Exportable review bundles"
    ]
  },
  {
    id: "reviewer",
    label: "REVIEWER",
    icon: ScanSearch,
    headline: "A consistent record that can survive diligence.",
    bullets: [
      "Stable game + round identifiers",
      "Verification status",
      "Administrative change context",
      "Audit trail linkage",
      "Reproducibility without private runtime access"
    ]
  }
];


const antiPatterns = [
  {
    bad: "“Trust our RNG.”",
    good: "Show the commitment, inputs, ruleset and reproduced result."
  },
  {
    bad: "“Support confirmed the round was correct.”",
    good: "Give support the same evidence path the player can inspect."
  },
  {
    bad: "“The frontend displayed it, so that must be the result.”",
    good: "Treat the UI as presentation and the server as authority."
  },
  {
    bad: "“Risk controls optimized the outcome.”",
    good: "Apply eligibility and wager controls before the supported first-party round resolves."
  },
  {
    bad: "“AI says the game was fair.”",
    good: "Let AI point to records; let deterministic evidence carry the claim."
  },
  {
    bad: "“Every third-party game works exactly like ours.”",
    good: "Scope first-party fairness claims to the systems Ethosino actually controls."
  }
];


const productTools = [
  {
    icon: Fingerprint,
    label: "IN-GAME VERIFIER",
    title: "Put the proof beside the game.",
    copy:
      "Verification should be reachable from the player surface, not buried in a technical whitepaper nobody sees during a real dispute."
  },
  {
    icon: FlaskConical,
    label: "SANDBOX REPLAY",
    title: "Reproduce without mutating production state.",
    copy:
      "Historical review belongs in a read-only environment where the round can be reconstructed without becoming another live game action."
  },
  {
    icon: ReceiptText,
    label: "ROUND RECEIPTS",
    title: "Make every important event attributable.",
    copy:
      "Game, ruleset, verification and settlement context can be presented as one coherent evidence story rather than disconnected database rows."
  },
  {
    icon: Database,
    label: "AUDIT EXPORTS",
    title: "Evidence should leave the dashboard cleanly.",
    copy:
      "Tenant-scoped bundles support disputes, reconciliation, partner diligence and internal review without relying on an ad hoc provider report."
  }
];


const truthBoundaries = [
  ["FIRST-PARTY GAMES", "Supported Ethosino-controlled games can expose deterministic verification evidence."],
  ["THIRD-PARTY CONTENT", "External providers may have their own fairness and certification model; Ethosino should not pretend to own what it does not."],
  ["LEGAL STATUS", "Fairness tooling supports an operator's diligence and player-trust posture. It does not create legal authority to operate."],
  ["SECURITY", "Verification should expose enough to reproduce the result without exposing protected secrets before they are safe to reveal."],
  ["RESPONSIBLE GAMING", "An ethical platform is broader than RNG: exclusion, wager policy and player controls matter too."],
  ["AI", "AI can summarize evidence and surface anomalies; it does not become RNG, settlement authority or a substitute for deterministic proof."]
];


const tickerItems = [
  "DON'T TRUST IT",
  "VERIFY IT",
  "COMMIT BEFORE RESOLUTION",
  "SERVER-AUTHORITATIVE OUTCOMES",
  "PLAYER-READABLE EVIDENCE",
  "TENANT-SCOPED ARTIFACTS",
  "REPLAYABLE FIRST-PARTY ROUNDS",
  "ETHICS AS PLATFORM BEHAVIOR"
];


/* ============================================================
   MOTION HELPERS
============================================================ */

const Reveal = ({
  children,
  className = "",
  direction = "up",
  delay = 0
}) => {
  const reduceMotion = useReducedMotion();

  const starts = {
    left: { x: -130, y: 0, rotate: -2.2 },
    right: { x: 130, y: 0, rotate: 2.2 },
    up: { x: 0, y: 80, rotate: 0 },
    topLeft: { x: -160, y: -110, rotate: -3.2 },
    topRight: { x: 160, y: -110, rotate: 3.2 },
    bottomLeft: { x: -160, y: 120, rotate: -3.2 },
    bottomRight: { x: 160, y: 120, rotate: 3.2 }
  };

  const start = starts[direction] || starts.up;

  return (
    <motion.div
      className={className}
      initial={
        reduceMotion
          ? false
          : {
              opacity: 0,
              ...start,
              scale: 0.95,
              filter: "blur(12px)"
            }
      }
      whileInView={{
        opacity: 1,
        x: 0,
        y: 0,
        rotate: 0,
        scale: 1,
        filter: "blur(0px)"
      }}
      viewport={{
        once: true,
        amount: 0.1
      }}
      transition={{
        duration: reduceMotion ? 0 : 0.9,
        delay,
        ease: [0.16, 1, 0.3, 1]
      }}
    >
      {children}
    </motion.div>
  );
};


/* ============================================================
   HERO RECEIPT
============================================================ */

const ProofReceipt = ({
  round,
  reduceMotion
}) => {
  return (
    <div className="eth-fair-v15-receipt-machine">

      <div className="eth-fair-v15-receipt-slot">
        <span />
        <span />
        <span />
      </div>


      <motion.div
        className="eth-fair-v15-receipt"
        animate={
          reduceMotion
            ? {}
            : {
                y: [0, 7, 0]
              }
        }
        transition={{
          duration: 5.3,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      >

        <div className="eth-fair-v15-receipt-holes">
          {Array.from({ length: 14 }).map((_, index) => (
            <span key={index} />
          ))}
        </div>


        <div className="eth-fair-v15-receipt-head">

          <div>
            <small>
              ETHOSINO / PROOF RECEIPT
            </small>

            <strong>
              {round.index}
            </strong>
          </div>

          <Stamp size={31} />

        </div>


        <div className="eth-fair-v15-receipt-rule" />


        <div className="eth-fair-v15-receipt-row">
          <span>
            GAME
          </span>
          <strong>
            {round.game}
          </strong>
        </div>

        <div className="eth-fair-v15-receipt-row">
          <span>
            TENANT
          </span>
          <strong>
            {round.tenant}
          </strong>
        </div>

        <div className="eth-fair-v15-receipt-row">
          <span>
            RULESET
          </span>
          <strong>
            {round.ruleset}
          </strong>
        </div>

        <div className="eth-fair-v15-receipt-row">
          <span>
            COMMITMENT
          </span>
          <strong>
            {round.commitment}
          </strong>
        </div>

        <div className="eth-fair-v15-receipt-row">
          <span>
            CLIENT SEED
          </span>
          <strong>
            {round.clientSeed}
          </strong>
        </div>

        <div className="eth-fair-v15-receipt-row">
          <span>
            NONCE
          </span>
          <strong>
            {round.nonce}
          </strong>
        </div>


        <div className="eth-fair-v15-receipt-rule eth-fair-v15-receipt-rule--dash" />


        <div className="eth-fair-v15-receipt-result">
          <small>
            REPRODUCED RESULT
          </small>

          <strong>
            {round.result}
          </strong>
        </div>


        <div className="eth-fair-v15-receipt-status">
          <span>
            VERIFICATION
          </span>

          <strong>
            <Check size={14} />
            {round.status}
          </strong>
        </div>


        <div className="eth-fair-v15-receipt-code">
          ||| || |||| | ||| || |||| ||
        </div>

      </motion.div>


      <motion.div
        className="eth-fair-v15-receipt-scan"
        animate={
          reduceMotion
            ? {}
            : {
                top: ["8%", "84%", "8%"]
              }
        }
        transition={{
          duration: 6.2,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />

    </div>
  );
};


/* ============================================================
   PAGE
============================================================ */

const OperatorFairnessModuleHelper = () => {
  const reduceMotion = useReducedMotion();

  const [
    activeRound,
    setActiveRound
  ] = useState(0);

  const [
    activeEthic,
    setActiveEthic
  ] = useState(0);

  const [
    activeEvidence,
    setActiveEvidence
  ] = useState(0);


  const round =
    proofRounds[activeRound];

  const ethic =
    ethicRules[activeEthic];

  const EthicIcon =
    ethic.icon;

  const evidence =
    evidenceReceipts[activeEvidence];

  const EvidenceIcon =
    evidence.icon;


  const {
    scrollYProgress
  } = useScroll();


  const progress =
    useSpring(
      scrollYProgress,
      {
        stiffness: 120,
        damping: 28,
        restDelta: 0.001
      }
    );


  const heroY =
    useTransform(
      scrollYProgress,
      [0, 0.16],
      [0, reduceMotion ? 0 : 64]
    );


  const heroRotate =
    useTransform(
      scrollYProgress,
      [0, 0.16],
      [0, reduceMotion ? 0 : -1.2]
    );


  const heroOpacity =
    useTransform(
      scrollYProgress,
      [0, 0.13],
      [1, 0.8]
    );


  const heroProof =
    useMemo(
      () => [
        ["CLAIM", "never enough"],
        ["EVIDENCE", "round specific"],
        ["REPLAY", "deterministic where supported"],
        ["AUTHORITY", "server side"]
      ],
      []
    );


  const nextRound = () => {
    setActiveRound(
      current =>
        (current + 1) %
        proofRounds.length
    );
  };


  const previousRound = () => {
    setActiveRound(
      current =>
        (current - 1 + proofRounds.length) %
        proofRounds.length
    );
  };


  return (
    <Fragment>

      <motion.div
        className="eth-fair-v15-scroll-progress"
        style={{
          scaleX: progress
        }}
      />


      <HeaderToMainPage />


      <main
        className="eth-fair-v15-page"
        id="ethosino-fairness-v15"
      >

        {/* ====================================================
            WORLD
        ==================================================== */}

        <div
          className="eth-fair-v15-world"
          aria-hidden="true"
        >
          <div className="eth-fair-v15-world-grid" />
          <div className="eth-fair-v15-world-noise" />
          <div className="eth-fair-v15-world-line eth-fair-v15-world-line--one" />
          <div className="eth-fair-v15-world-line eth-fair-v15-world-line--two" />
          <div className="eth-fair-v15-world-glow eth-fair-v15-world-glow--green" />
          <div className="eth-fair-v15-world-glow eth-fair-v15-world-glow--red" />
        </div>


        {/* ====================================================
            HERO — PROOF RECEIPT / MANIFESTO
        ==================================================== */}

        <section className="eth-fair-v15-hero">

          <motion.div
            className="eth-fair-v15-hero-parallax"
            style={{
              y: heroY,
              rotate: heroRotate,
              opacity: heroOpacity
            }}
          >

            <div className="eth-fair-v15-shell">

              <div className="eth-fair-v15-hero-topline">

                <motion.div
                  initial={
                    reduceMotion
                      ? false
                      : {
                          opacity: 0,
                          x: -55
                        }
                  }
                  animate={{
                    opacity: 1,
                    x: 0
                  }}
                  transition={{
                    duration: 0.62
                  }}
                >
                  <span className="eth-fair-v15-live-dot" />

                  FAIRNESS TOOLING / ETHICS / VERIFICATION
                </motion.div>


                <motion.span
                  initial={
                    reduceMotion
                      ? false
                      : {
                          opacity: 0,
                          x: 55
                        }
                  }
                  animate={{
                    opacity: 1,
                    x: 0
                  }}
                  transition={{
                    duration: 0.62,
                    delay: 0.1
                  }}
                >
                  CLAIMS ARE CHEAP
                </motion.span>

              </div>


              <div className="eth-fair-v15-hero-grid">

                <div className="eth-fair-v15-hero-copy">

                  <motion.div
                    className="eth-fair-v15-hero-mark"
                    initial={
                      reduceMotion
                        ? false
                        : {
                            opacity: 0,
                            y: -25
                          }
                    }
                    animate={{
                      opacity: 1,
                      y: 0
                    }}
                    transition={{
                      duration: 0.6
                    }}
                  >
                    ETHOSINO PRINCIPLE / 001
                  </motion.div>


                  <motion.h1
                    initial={
                      reduceMotion
                        ? false
                        : {
                            opacity: 0,
                            x: -125,
                            y: 48,
                            rotate: -2.5,
                            filter: "blur(9px)"
                          }
                    }
                    animate={{
                      opacity: 1,
                      x: 0,
                      y: 0,
                      rotate: 0,
                      filter: "blur(0px)"
                    }}
                    transition={{
                      duration: 0.96,
                      ease: [0.16, 1, 0.3, 1]
                    }}
                  >
                    DON'T TRUST IT.
                  </motion.h1>


                  <motion.h2
                    initial={
                      reduceMotion
                        ? false
                        : {
                            opacity: 0,
                            x: 135,
                            rotate: 2.5,
                            filter: "blur(9px)"
                          }
                    }
                    animate={{
                      opacity: 1,
                      x: 0,
                      rotate: -1.5,
                      filter: "blur(0px)"
                    }}
                    transition={{
                      duration: 0.98,
                      delay: 0.08,
                      ease: [0.16, 1, 0.3, 1]
                    }}
                  >
                    VERIFY IT.
                  </motion.h2>


                  <motion.p
                    className="eth-fair-v15-hero-lead"
                    initial={
                      reduceMotion
                        ? false
                        : {
                            opacity: 0,
                            y: 30
                          }
                    }
                    animate={{
                      opacity: 1,
                      y: 0
                    }}
                    transition={{
                      duration: 0.72,
                      delay: 0.17
                    }}
                  >
                    Ethosino's fairness philosophy starts from an uncomfortable
                    premise: a gaming platform should not ask a player to accept
                    “trust us” as evidence. Supported first-party games are built
                    so the round can carry the artifacts needed to inspect,
                    reproduce and explain what happened — without giving the
                    player pre-round access to protected secrets.
                  </motion.p>


                  <motion.div
                    className="eth-fair-v15-hero-manifesto"
                    initial={
                      reduceMotion
                        ? false
                        : {
                            opacity: 0,
                            x: -50
                          }
                    }
                    animate={{
                      opacity: 1,
                      x: 0
                    }}
                    transition={{
                      duration: 0.68,
                      delay: 0.26
                    }}
                  >
                    <Fingerprint size={19} />

                    <span>
                      The goal is not to ask players to trust Ethosino more.{" "}

                      <strong>
                        It is to give them less they need to trust blindly.
                      </strong>
                    </span>
                  </motion.div>


                  <motion.div
                    className="eth-fair-v15-hero-actions"
                    initial={
                      reduceMotion
                        ? false
                        : {
                            opacity: 0,
                            y: 24
                          }
                    }
                    animate={{
                      opacity: 1,
                      y: 0
                    }}
                    transition={{
                      duration: 0.62,
                      delay: 0.34
                    }}
                  >

                    <a
                      href="#proof-lab"
                      className="eth-fair-v15-btn eth-fair-v15-btn--primary"
                    >
                      Inspect the proof model
                      <ArrowRight size={17} />
                    </a>


                    <Link
                      to="/book-demo"
                      className="eth-fair-v15-btn eth-fair-v15-btn--ghost"
                    >
                      See verifier surfaces
                    </Link>

                  </motion.div>

                </div>


                <motion.div
                  className="eth-fair-v15-hero-proof"
                  initial={
                    reduceMotion
                      ? false
                      : {
                          opacity: 0,
                          x: 120,
                          y: -65,
                          rotate: 3,
                          scale: 0.92
                        }
                  }
                  animate={{
                    opacity: 1,
                    x: 0,
                    y: 0,
                    rotate: 0,
                    scale: 1
                  }}
                  transition={{
                    duration: 1.02,
                    delay: 0.1,
                    ease: [0.16, 1, 0.3, 1]
                  }}
                >

                  <ProofReceipt
                    round={round}
                    reduceMotion={reduceMotion}
                  />


                  <div className="eth-fair-v15-round-switcher">

                    <button
                      type="button"
                      onClick={previousRound}
                      aria-label="Previous proof receipt"
                    >
                      <ChevronLeft size={16} />
                    </button>

                    <div>
                      {proofRounds.map(
                        (
                          item,
                          index
                        ) => (
                          <button
                            key={item.id}
                            type="button"
                            className={
                              activeRound === index
                                ? "is-active"
                                : ""
                            }
                            onClick={() =>
                              setActiveRound(index)
                            }
                          >
                            {item.game}
                          </button>
                        )
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={nextRound}
                      aria-label="Next proof receipt"
                    >
                      <ChevronRight size={16} />
                    </button>

                  </div>

                </motion.div>

              </div>


              <div className="eth-fair-v15-hero-proofline">

                {heroProof.map(
                  (
                    item,
                    index
                  ) => (
                    <motion.div
                      key={item[0]}
                      initial={
                        reduceMotion
                          ? false
                          : {
                              opacity: 0,
                              y: 28
                            }
                      }
                      animate={{
                        opacity: 1,
                        y: 0
                      }}
                      transition={{
                        duration: 0.5,
                        delay: 0.39 + index * 0.05
                      }}
                    >
                      <span>
                        0{index + 1} / {item[0]}
                      </span>

                      <strong>
                        {item[1]}
                      </strong>
                    </motion.div>
                  )
                )}

              </div>

            </div>

          </motion.div>


          <div className="eth-fair-v15-ticker">

            <div className="eth-fair-v15-ticker-track">

              {[...tickerItems, ...tickerItems].map(
                (
                  item,
                  index
                ) => (
                  <React.Fragment key={`${item}-${index}`}>
                    <span>
                      {item}
                    </span>

                    <i>
                      ◆
                    </i>
                  </React.Fragment>
                )
              )}

            </div>

          </div>

        </section>


        {/* ====================================================
            TRUST / PROOF SPLIT
        ==================================================== */}

        <section className="eth-fair-v15-trust">

          <div className="eth-fair-v15-shell">

            <Reveal
              direction="topLeft"
              className="eth-fair-v15-trust-copy"
            >

              <span>
                THE ETHICAL STARTING POINT
              </span>

              <h2>
                Trust is an outcome.{" "}

                <strong>
                  Not an input.
                </strong>
              </h2>

              <p>
                Gaming asks the player to accept invisible machinery: random
                inputs, server state, financial settlement and operator policy.
                Ethosino's answer is not “believe us.” The answer is to move as
                much of that relationship as possible from assertion into
                inspectable system behavior.
              </p>

            </Reveal>


            <div className="eth-fair-v15-trust-stage">

              <Reveal
                direction="bottomLeft"
                className="eth-fair-v15-trust-word eth-fair-v15-trust-word--trust"
              >
                TRUST
              </Reveal>


              <Reveal
                direction="topRight"
                className="eth-fair-v15-trust-arrow"
              >
                <ArrowRight size={26} />
                <span>
                  EARNED BY EVIDENCE
                </span>
              </Reveal>


              <Reveal
                direction="bottomRight"
                className="eth-fair-v15-trust-word eth-fair-v15-trust-word--proof"
              >
                PROOF
              </Reveal>

            </div>


            <Reveal
              direction="right"
              className="eth-fair-v15-trust-foot"
            >
              <Scale size={20} />

              <span>
                Ethical product design means deciding which promises can become
                software behavior — and then making them software behavior.
              </span>
            </Reveal>

          </div>
        </section>


        {/* ====================================================
            ETHICS COMPILED
        ==================================================== */}

        <section className="eth-fair-v15-ethics">

          <div className="eth-fair-v15-shell">

            <Reveal
              direction="topRight"
              className="eth-fair-v15-section-head"
            >
              <span>
                ETHICS, COMPILED
              </span>

              <h2>
                Values matter more when{" "}

                <strong>
                  the code has to obey them.
                </strong>
              </h2>

              <p>
                These are not slogans sitting above the architecture. Each
                principle maps to a technical boundary, product decision or
                governance rule.
              </p>
            </Reveal>


            <div className="eth-fair-v15-ethic-layout">

              <Reveal
                direction="left"
                className="eth-fair-v15-ethic-rail"
              >

                {ethicRules.map(
                  (
                    rule,
                    index
                  ) => {
                    const Icon =
                      rule.icon;

                    return (
                      <motion.button
                        key={rule.id}
                        type="button"
                        className={
                          activeEthic === index
                            ? "is-active"
                            : ""
                        }
                        onClick={() =>
                          setActiveEthic(index)
                        }
                        whileHover={{
                          x: 7
                        }}
                        whileTap={{
                          scale: 0.985
                        }}
                      >

                        <span>
                          {rule.number}
                        </span>

                        <div>
                          <Icon size={18} />
                        </div>

                        <strong>
                          {rule.title}
                        </strong>

                        <ChevronRight size={15} />

                      </motion.button>
                    );
                  }
                )}

              </Reveal>


              <Reveal
                direction="right"
                className="eth-fair-v15-ethic-stage"
              >

                <AnimatePresence mode="wait">

                  <motion.article
                    key={ethic.id}
                    className="eth-fair-v15-ethic-sheet"
                    initial={
                      reduceMotion
                        ? false
                        : {
                            opacity: 0,
                            x: 85,
                            rotate: 2.2,
                            filter: "blur(10px)"
                          }
                    }
                    animate={{
                      opacity: 1,
                      x: 0,
                      rotate: 0,
                      filter: "blur(0px)"
                    }}
                    exit={
                      reduceMotion
                        ? undefined
                        : {
                            opacity: 0,
                            x: -70,
                            rotate: -2,
                            filter: "blur(8px)"
                          }
                    }
                    transition={{
                      duration: 0.4,
                      ease: [0.16, 1, 0.3, 1]
                    }}
                  >

                    <div className="eth-fair-v15-ethic-sheet-stamp">
                      {ethic.number}
                    </div>


                    <div className="eth-fair-v15-ethic-sheet-icon">
                      <EthicIcon size={32} />
                    </div>


                    <small>
                      PRINCIPLE / {ethic.number}
                    </small>

                    <h3>
                      {ethic.title}
                    </h3>


                    <div className="eth-fair-v15-ethic-sheet-claim">

                      <span>
                        ETHICAL CLAIM
                      </span>

                      <p>
                        {ethic.statement}
                      </p>

                    </div>


                    <div className="eth-fair-v15-ethic-sheet-code">

                      <span>
                        SOFTWARE BOUNDARY
                      </span>

                      <p>
                        {ethic.implementation}
                      </p>

                    </div>


                    <div className="eth-fair-v15-ethic-sheet-foot">
                      <Code2 size={16} />
                      values → constraints → behavior → evidence
                    </div>

                  </motion.article>

                </AnimatePresence>

              </Reveal>

            </div>

          </div>
        </section>


        {/* ====================================================
            PROOF LAB
        ==================================================== */}

        <section
          className="eth-fair-v15-prooflab"
          id="proof-lab"
        >

          <div className="eth-fair-v15-shell">

            <Reveal
              direction="topLeft"
              className="eth-fair-v15-section-head eth-fair-v15-section-head--light"
            >
              <span>
                THE PROOF LAB
              </span>

              <h2>
                A round should be able to{" "}

                <strong>
                  tell its own story.
                </strong>
              </h2>

              <p>
                Commit–reveal is useful because it turns “we did not change the
                result afterward” into something that can be anchored and later
                checked. The verifier then connects that cryptographic posture to
                an understandable product flow.
              </p>
            </Reveal>


            <div className="eth-fair-v15-lifecycle">

              <div className="eth-fair-v15-lifecycle-line" />

              {lifecycle.map(
                (
                  stage,
                  index
                ) => {
                  const Icon =
                    stage.icon;

                  return (
                    <Reveal
                      key={stage.key}
                      direction={
                        index % 2 === 0
                          ? "topLeft"
                          : "bottomRight"
                      }
                      delay={index * 0.04}
                    >

                      <article className="eth-fair-v15-lifecycle-step">

                        <div className="eth-fair-v15-lifecycle-index">
                          0{index + 1}
                        </div>

                        <div className="eth-fair-v15-lifecycle-icon">
                          <Icon size={22} />
                        </div>

                        <span>
                          {stage.key}
                        </span>

                        <h3>
                          {stage.line}
                        </h3>

                        <p>
                          {stage.copy}
                        </p>

                      </article>

                    </Reveal>
                  );
                }
              )}


              <motion.i
                className="eth-fair-v15-lifecycle-packet"
                animate={
                  reduceMotion
                    ? {}
                    : {
                        left: ["1%", "97%"]
                      }
                }
                transition={{
                  duration: 7.4,
                  repeat: Infinity,
                  ease: "linear"
                }}
              />

            </div>


            <Reveal
              direction="right"
              className="eth-fair-v15-prooflab-equation"
            >
              <span>
                COMMIT
              </span>

              <b>
                +
              </b>

              <span>
                CANONICAL INPUTS
              </span>

              <b>
                +
              </b>

              <span>
                RULESET
              </span>

              <b>
                →
              </b>

              <strong>
                REPRODUCIBLE RESULT
              </strong>
            </Reveal>

          </div>
        </section>


        {/* ====================================================
            WHO GETS WHAT EVIDENCE
        ==================================================== */}

        <section className="eth-fair-v15-evidence">

          <div className="eth-fair-v15-shell">

            <Reveal
              direction="topRight"
              className="eth-fair-v15-section-head"
            >
              <span>
                ONE ROUND / DIFFERENT READERS
              </span>

              <h2>
                The same evidence should work{" "}

                <strong>
                  at more than one depth.
                </strong>
              </h2>

              <p>
                A player needs clarity. Support needs speed. Compliance and
                diligence teams need lineage. The underlying round should not
                require three contradictory stories.
              </p>
            </Reveal>


            <div className="eth-fair-v15-evidence-layout">

              <Reveal
                direction="left"
                className="eth-fair-v15-evidence-tabs"
              >

                {evidenceReceipts.map(
                  (
                    item,
                    index
                  ) => {
                    const Icon =
                      item.icon;

                    return (
                      <button
                        key={item.id}
                        type="button"
                        className={
                          activeEvidence === index
                            ? "is-active"
                            : ""
                        }
                        onClick={() =>
                          setActiveEvidence(index)
                        }
                      >
                        <span>
                          0{index + 1}
                        </span>

                        <Icon size={18} />

                        <strong>
                          {item.label}
                        </strong>
                      </button>
                    );
                  }
                )}

              </Reveal>


              <Reveal
                direction="bottomRight"
                className="eth-fair-v15-evidence-stage"
              >

                <AnimatePresence mode="wait">

                  <motion.article
                    key={evidence.id}
                    className="eth-fair-v15-evidence-dossier"
                    initial={
                      reduceMotion
                        ? false
                        : {
                            opacity: 0,
                            y: 48,
                            rotate: 1.5,
                            filter: "blur(9px)"
                          }
                    }
                    animate={{
                      opacity: 1,
                      y: 0,
                      rotate: 0,
                      filter: "blur(0px)"
                    }}
                    exit={
                      reduceMotion
                        ? undefined
                        : {
                            opacity: 0,
                            y: -35,
                            rotate: -1.5,
                            filter: "blur(8px)"
                          }
                    }
                    transition={{
                      duration: 0.36
                    }}
                  >

                    <div className="eth-fair-v15-evidence-dossier-head">

                      <EvidenceIcon size={27} />

                      <div>
                        <span>
                          EVIDENCE VIEW / {evidence.label}
                        </span>

                        <strong>
                          {evidence.headline}
                        </strong>
                      </div>

                    </div>


                    <div className="eth-fair-v15-evidence-dossier-list">

                      {evidence.bullets.map(
                        item => (
                          <span key={item}>
                            <Check size={13} />
                            {item}
                          </span>
                        )
                      )}

                    </div>


                    <div className="eth-fair-v15-evidence-dossier-foot">
                      <ReceiptText size={16} />
                      same round • appropriate depth • consistent story
                    </div>

                  </motion.article>

                </AnimatePresence>

              </Reveal>

            </div>

          </div>
        </section>


        {/* ====================================================
            DON'T DO THIS
        ==================================================== */}

        <section className="eth-fair-v15-antipatterns">

          <div className="eth-fair-v15-shell">

            <Reveal
              direction="bottomLeft"
              className="eth-fair-v15-section-head eth-fair-v15-section-head--light"
            >
              <span>
                FAIRNESS ANTI-PATTERNS
              </span>

              <h2>
                If the answer begins with{" "}

                <strong>
                  “trust us,” keep asking.
                </strong>
              </h2>
            </Reveal>


            <div className="eth-fair-v15-antipattern-list">

              {antiPatterns.map(
                (
                  item,
                  index
                ) => (
                  <Reveal
                    key={item.bad}
                    direction={
                      index % 2 === 0
                        ? "left"
                        : "right"
                    }
                    delay={index * 0.025}
                  >
                    <div className="eth-fair-v15-antipattern-row">

                      <div className="eth-fair-v15-antipattern-bad">
                        <X size={16} />
                        <span>
                          {item.bad}
                        </span>
                      </div>

                      <ArrowRight size={18} />

                      <div className="eth-fair-v15-antipattern-good">
                        <Check size={16} />
                        <span>
                          {item.good}
                        </span>
                      </div>

                    </div>
                  </Reveal>
                )
              )}

            </div>

          </div>
        </section>


        {/* ====================================================
            TOOLING
        ==================================================== */}

        <section className="eth-fair-v15-tooling">

          <div className="eth-fair-v15-shell">

            <Reveal
              direction="topLeft"
              className="eth-fair-v15-section-head"
            >
              <span>
                FAIRNESS AS PRODUCT
              </span>

              <h2>
                Proof is not useful if{" "}

                <strong>
                  nobody can reach it.
                </strong>
              </h2>

              <p>
                Ethosino's fairness tooling turns the verification posture into
                actual product surfaces for players, operators and reviewers.
              </p>
            </Reveal>


            <div className="eth-fair-v15-tool-grid">

              {productTools.map(
                (
                  tool,
                  index
                ) => {
                  const Icon =
                    tool.icon;

                  const directions = [
                    "topLeft",
                    "topRight",
                    "bottomLeft",
                    "bottomRight"
                  ];

                  return (
                    <Reveal
                      key={tool.label}
                      direction={directions[index]}
                      delay={index * 0.04}
                    >
                      <article className="eth-fair-v15-tool-card">

                        <div className="eth-fair-v15-tool-card-icon">
                          <Icon size={23} />
                        </div>

                        <span>
                          {tool.label}
                        </span>

                        <h3>
                          {tool.title}
                        </h3>

                        <p>
                          {tool.copy}
                        </p>

                        <div className="eth-fair-v15-tool-card-corner">
                          0{index + 1}
                        </div>

                      </article>
                    </Reveal>
                  );
                }
              )}

            </div>

          </div>
        </section>


        {/* ====================================================
            ETHICS BEYOND RNG
        ==================================================== */}

        <section className="eth-fair-v15-beyond">

          <div className="eth-fair-v15-shell">

            <Reveal
              direction="topRight"
              className="eth-fair-v15-beyond-intro"
            >

              <span>
                ETHICS BEYOND RNG
              </span>

              <h2>
                A fair result does not make{" "}

                <strong>
                  the entire system ethical.
                </strong>
              </h2>

              <p>
                Ethosino's product philosophy is broader than cryptographic
                verification. Player protection, operator authority, auditability
                and clear software boundaries matter too.
              </p>

            </Reveal>


            <div className="eth-fair-v15-beyond-track">

              <Reveal direction="left">
                <div className="eth-fair-v15-beyond-item">
                  <UserCheck size={22} />
                  <span>
                    SELF-EXCLUSION
                  </span>
                  <strong>
                    Easy to initiate. Hard for the platform to ignore.
                  </strong>
                </div>
              </Reveal>

              <Reveal direction="right">
                <div className="eth-fair-v15-beyond-item">
                  <Gauge size={22} />
                  <span>
                    WAGER POLICY
                  </span>
                  <strong>
                    Exposure controls enforced before the round.
                  </strong>
                </div>
              </Reveal>

              <Reveal direction="left">
                <div className="eth-fair-v15-beyond-item">
                  <Server size={22} />
                  <span>
                    SERVER AUTHORITY
                  </span>
                  <strong>
                    The browser cannot award itself a different truth.
                  </strong>
                </div>
              </Reveal>

              <Reveal direction="right">
                <div className="eth-fair-v15-beyond-item">
                  <Database size={22} />
                  <span>
                    AUDITABILITY
                  </span>
                  <strong>
                    Consequential actions should leave an attributable record.
                  </strong>
                </div>
              </Reveal>

            </div>


            <Reveal
              direction="bottomRight"
              className="eth-fair-v15-beyond-quote"
            >
              <BookOpenCheck size={22} />

              <span>
                Fairness is not one algorithm. It is what happens when game
                logic, money movement, player controls and operator authority all
                have boundaries the platform is expected to respect.
              </span>
            </Reveal>

          </div>
        </section>


        {/* ====================================================
            TRUTH BOUNDARIES
        ==================================================== */}

        <section className="eth-fair-v15-boundaries">

          <div className="eth-fair-v15-shell">

            <Reveal
              direction="bottomLeft"
              className="eth-fair-v15-section-head eth-fair-v15-section-head--light"
            >
              <span>
                SAY EXACTLY WHAT THE SOFTWARE PROVES
              </span>

              <h2>
                Transparency includes{" "}

                <strong>
                  admitting the boundary.
                </strong>
              </h2>

              <p>
                Ethical positioning should be precise. Ethosino should make
                strong claims where the platform owns the evidence and restrained
                claims where it does not.
              </p>
            </Reveal>


            <div className="eth-fair-v15-boundary-grid">

              {truthBoundaries.map(
                (
                  item,
                  index
                ) => (
                  <Reveal
                    key={item[0]}
                    direction={
                      index % 2 === 0
                        ? "topLeft"
                        : "bottomRight"
                    }
                    delay={index * 0.03}
                  >
                    <article className="eth-fair-v15-boundary-card">

                      <span>
                        0{index + 1}
                      </span>

                      <small>
                        {item[0]}
                      </small>

                      <p>
                        {item[1]}
                      </p>

                    </article>
                  </Reveal>
                )
              )}

            </div>

          </div>
        </section>


        {/* ====================================================
            FINAL
        ==================================================== */}

        <section className="eth-fair-v15-final">

          <div className="eth-fair-v15-shell">

            <Reveal
              direction="bottomRight"
              className="eth-fair-v15-final-card"
            >

              <div className="eth-fair-v15-final-grid" />


              <div className="eth-fair-v15-final-copy">

                <span>
                  DON'T TRUST ETHOSINO BECAUSE THE WEBSITE SAYS “FAIR.”
                </span>

                <h2>
                  Verify the round.{" "}

                  <strong>
                    Then decide what deserves trust.
                  </strong>
                </h2>

                <p>
                  See the player-facing verifier, deterministic replay posture,
                  round history, tenant boundaries and audit tooling in the
                  working platform. The strongest fairness claim is the one you
                  can inspect yourself.
                </p>


                <div className="eth-fair-v15-final-actions">

                  <Link
                    to="/book-demo"
                    className="eth-fair-v15-btn eth-fair-v15-btn--primary eth-fair-v15-btn--large"
                  >
                    Inspect the verifier live
                    <ArrowRight size={18} />
                  </Link>


                  <Link
                    to="/request-documentation"
                    className="eth-fair-v15-btn eth-fair-v15-btn--ghost eth-fair-v15-btn--large"
                  >
                    Request fairness architecture
                  </Link>

                </div>

              </div>


              <div className="eth-fair-v15-final-seal">

                <motion.div
                  animate={
                    reduceMotion
                      ? {}
                      : {
                          rotate: 360
                        }
                  }
                  transition={{
                    duration: 22,
                    repeat: Infinity,
                    ease: "linear"
                  }}
                >
                  <span>
                    VERIFY
                  </span>
                  <span>
                    REPLAY
                  </span>
                  <span>
                    INSPECT
                  </span>
                  <span>
                    EXPLAIN
                  </span>
                </motion.div>


                <div>
                  <Fingerprint size={36} />

                  <span>
                    ETHOSINO
                  </span>

                  <strong>
                    PROOF FIRST
                  </strong>
                </div>

              </div>

            </Reveal>

          </div>
        </section>

      </main>


      <Footer />

    </Fragment>
  );
};


export default OperatorFairnessModuleHelper;
